import { InjectionToken } from '@angular/core'

/**
 * Per-application shape of the assistant feature.
 *
 * The same page component serves Fleet, TSP and TI. What differs between them is not
 * the chat itself but the shell it lives in: Fleet embeds the chat in a collapsible
 * sidebar layout and can minimize it into a floating dialog, while the admin applications
 * render it as an ordinary page under their top menubar and have neither affordance.
 */
export interface AssistantConfig {
  /**
   * Absolute router path the page owns, used to build thread deep links.
   * Every application currently mounts the feature at the same path;
   * it is a setting only so that the page never has to hardcode it.
   */
  routeBase: string

  /**
   * Whether the chat can be minimized into the floating dialog rendered by the application
   * layout. Fleet only — the admin applications use the standalone page exclusively, so the
   * minimize button is hidden and the page never reports its fullscreen state to the facade.
   */
  allowDialogMode: boolean

  /**
   * Whether the application layout provides the collapsible sidebar
   * driven by `SidebarService`. Controls the mobile hamburger in the page
   * header, which would otherwise open a sidebar that does not exist.
   */
  hasSidebar: boolean

  /**
   * Whether the standalone assistant page header is visible.
   */
  showHeader: boolean

  /**
   * Whether file attachments are offered on production. Temporary: they are released to TI first,
   * while every other environment offers them in every application.
   */
  attachmentsOnProduction: boolean

  /**
   * TI Admin only: the list is the TI team's shared inbox, headed "Team conversations", with a
   * switch to the user's own threads and to the support emails. Team is the default.
   */
  teamThreads: boolean

  /**
   * TI Admin only: the page of what the assistant remembers. The thread header links to it, in a
   * new tab, for the staff who may manage memory; null hides the link.
   */
  memoryUrl: string | null
}

/**
 * Standalone-page behaviour with no host-layout integration. This is the
 * safe default: an application that forgets to call `provideAssistant()`
 * gets a working page rather than buttons wired to services it does not have.
 */
export const DEFAULT_ASSISTANT_CONFIG: AssistantConfig = {
  routeBase: '/ai-support',
  allowDialogMode: false,
  hasSidebar: false,
  showHeader: true,
  attachmentsOnProduction: false,
  teamThreads: false,
  memoryUrl: null,
}

export const ASSISTANT_CONFIG = new InjectionToken<AssistantConfig>('AssistantConfig', {
  providedIn: 'root',
  factory: () => DEFAULT_ASSISTANT_CONFIG,
})
